/**
 * Compass Rose Component
 * Displays a N/S/E/W compass indicator overlay for map orientation.
 * Supports a northOffsetDegrees prop to rotate the compass so that
 * the N label aligns with the map's geographic north direction.
 *
 * The arrow and label positions rotate, but the text itself stays
 * upright so it remains readable at any rotation.
 */

interface CompassRoseProps {
  /** Size of the compass in pixels (default: 48) */
  size?: number;
  /** Rotation offset in degrees so N aligns with the map's north (default: 0) */
  northOffsetDegrees?: number;
  /** Additional CSS classes */
  className?: string;
}

/** Convert degrees to radians */
function degToRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

/** Cardinal direction definition */
interface Cardinal {
  label: string;
  /** Angle from 12 o'clock (0°=top) before applying offset */
  baseAngle: number;
  bold: boolean;
  color: string;
}

const CARDINALS: Cardinal[] = [
  { label: 'N', baseAngle: 0, bold: true, color: '#b91c1c' },
  { label: 'E', baseAngle: 90, bold: false, color: '#475569' },
  { label: 'S', baseAngle: 180, bold: false, color: '#475569' },
  { label: 'W', baseAngle: 270, bold: false, color: '#475569' },
];

export function CompassRose({
  size = 48,
  northOffsetDegrees = 0,
  className,
}: CompassRoseProps) {
  const center = size / 2;
  const radius = size / 2 - 2;
  const arrowLen = radius * 0.55;
  const labelRadius = radius * 0.82;
  const fontSize = size * 0.2;
  const smallFontSize = size * 0.16;

  return (
    <div
      className={className}
      style={{ pointerEvents: 'none' }}
      aria-label={`Compass: North is at ${northOffsetDegrees}° from top`}
    >
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        xmlns="http://www.w3.org/2000/svg"
      >
        {/* Semi-transparent background circle */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="white"
          fillOpacity={0.85}
          stroke="#64748b"
          strokeWidth={1}
        />

        {/* Rotated arrows only */}
        <g transform={`rotate(${northOffsetDegrees}, ${center}, ${center})`}>
          {/* North arrow (red) */}
          <polygon
            points={`${center},${center - arrowLen} ${center - arrowLen * 0.2},${center} ${center + arrowLen * 0.2},${center}`}
            fill="#b91c1c"
            fillOpacity={0.9}
          />
          {/* South arrow (lighter) */}
          <polygon
            points={`${center},${center + arrowLen} ${center - arrowLen * 0.2},${center} ${center + arrowLen * 0.2},${center}`}
            fill="#cbd5e1"
            fillOpacity={0.8}
          />
          {/* Small center dot */}
          <circle cx={center} cy={center} r={size * 0.04} fill="#334155" />
        </g>

        {/* Cardinal labels — positioned around the rotated circle but text stays upright */}
        {CARDINALS.map(({ label, baseAngle, bold, color }) => {
          // The final angle for this label on screen (CSS/SVG: 0°=3 o'clock, but
          // we want 0°=12 o'clock, so subtract 90° before converting).
          const angleDeg = baseAngle + northOffsetDegrees;
          const angleRad = degToRad(angleDeg - 90);
          const lx = center + labelRadius * Math.cos(angleRad);
          const ly = center + labelRadius * Math.sin(angleRad);

          return (
            <text
              key={label}
              x={lx}
              y={ly}
              textAnchor="middle"
              dominantBaseline="central"
              fontSize={bold ? fontSize : smallFontSize}
              fontWeight={bold ? 'bold' : '500'}
              fill={color}
            >
              {label}
            </text>
          );
        })}
      </svg>
    </div>
  );
}
